import React, { useEffect, useRef, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { closeSessionModal, MUSIC_OPTIONS, setPauseMusic, setSessionView } from "../../store/ProductivitySessionSlice";
import { Button } from "@mui/material";
import "./SessionProgress.css";
import { useTranslation } from "react-i18next";
import { setOpenModal } from "../../../ControlPanel/store/controlPanelSlice";

function useMobile() {
    const [isMobile, setIsMobile] = useState(false);
    useEffect(() => {
        if (typeof window === "undefined") return;
        const handleResize = () => setIsMobile(window.innerWidth < 768);
        handleResize();
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);
    return isMobile;
}

export default function SessionProgress() {
    const dispatch = useDispatch();
    const { t } = useTranslation();
    const {
        sessionView, subject, sessionDuration, sessionStartTime,
        selectedTrack, isMusicEnabled
    } = useSelector((state) => state.productivitySession);
    const { openModal } = useSelector((state) => state.controlPanel);
    const { currentRoom } = useSelector((state) => state.MapDisplay);
    const { localAvatar } = useSelector((state) => state.auth);
    const { selectedSpeaker } = useSelector(state => state.mediaControls);
    const [progress, setProgress] = useState(0);
    const [timeLeft, setTimeLeft] = useState("");
    const isMobile = useMobile();
    const [isExpanded, setIsExpanded] = useState(false);
    const audioRef = useRef(null);
    const [isMusicPlaying, setIsMusicPlaying] = useState(true);
    const currentTrackData = MUSIC_OPTIONS.find(t => t.id === selectedTrack);
    useEffect(() => {
        if (!audioRef.current) {
            audioRef.current = new Audio();
            audioRef.current.volume = 0.5;
        }

        const audio = audioRef.current;
        const deviceId = selectedSpeaker?.deviceId;

        const playAudio = async () => {
            if (sessionView !== 'progress') {
                audio.pause();
                return;
            }

            if (!isMusicEnabled || !selectedTrack || !currentTrackData) {
                audio.pause();
                return;
            }

            try {
                if (audio.src !== currentTrackData.src || audio.paused) {
                    audio.src = currentTrackData.src;
                    audio.loop = true;
                    await audio.load();
                }

                if (deviceId && typeof audio.setSinkId === 'function') {
                    await audio.setSinkId(deviceId).catch(e => console.warn(e));
                }

                if (isMusicPlaying) {
                    const playPromise = audio.play();
                    if (playPromise !== undefined) {
                        playPromise.catch(error => {
                            console.warn("Autoplay prevented:", error);
                            setIsMusicPlaying(false);
                        });
                    }
                } else {
                    audio.pause();
                }

            } catch (err) {
                console.error("Audio Error:", err);
            }
        };

        playAudio();

    }, [selectedTrack, isMusicEnabled, isMusicPlaying, selectedSpeaker?.deviceId, currentTrackData, sessionView]);

    useEffect(() => {
        return () => {
            if (audioRef.current) {
                audioRef.current.pause();
                audioRef.current.src = "";
            }
        };
    }, []);

    useEffect(() => {
        if (!sessionDuration || !sessionStartTime) return;
        const durationInMs = sessionDuration * 60 * 1000;
        const updateTimer = () => {
            const now = Date.now();
            const elapsed = now - sessionStartTime;
            const percent = Math.min((elapsed / durationInMs) * 100, 100);
            setProgress(percent);
            const remainingMs = Math.max(0, durationInMs - elapsed);
             const totalSeconds = Math.ceil(remainingMs / 1000);
             const mins = Math.floor(totalSeconds / 60);
            const secs = totalSeconds % 60;
            setTimeLeft(`${mins} ${t("mins")} ${secs < 10 ? '0' : ''}${secs} ${t("secs")}`);
            if (percent >= 100) {
                if (openModal === "track_selection") {
                    dispatch(setOpenModal(null));
                }
                dispatch(closeSessionModal());
            }
        };
        updateTimer();
        const interval = setInterval(updateTimer, 1000);
        return () => clearInterval(interval);
    }, [sessionDuration, sessionStartTime]);

    useEffect(() => {
        if (sessionView === "progress" && localAvatar?.focus_state === 0) {
            dispatch(closeSessionModal());
        }
    }, [localAvatar]);

    const handleStopSession = () => {
        setIsMusicPlaying(true);
        dispatch(closeSessionModal());
        dispatch(setSessionView("create"));
        setIsExpanded(false);
    };

    const handleTrackChange = () => {
        if (isMusicPlaying) {
            setIsMusicPlaying(false);
        }
        dispatch(setPauseMusic(false));
        dispatch(setOpenModal("track_selection"));
    };

    useEffect(() => {
        if (isMusicEnabled) {
            setIsMusicPlaying(true);
        }
    }, [isMusicEnabled]);
    const handleMinimize = () => setIsExpanded(false);
    const size = 45;
    const strokeWidth = 2;
    const center = size / 2;
    const radius = 20;
    const circumference = 2 * Math.PI * radius;
    const strokeDashoffset = circumference - (progress / 100) * circumference;

    const renderSessionModal = () => (
        <div className="coffe-pause-overlay" onClick={() => setIsExpanded(false)}>
                <div className="coffe-pause-modal-centered" onClick={(e) => e.stopPropagation()}>
                <div className="session-header">
                    <div className="session-header-left">
                        <span className="material-symbols-outlined session-header-icon">headphones</span>
                        <div className="session-header-title">
                            <span className="session-header-title-text">{t("productivity_session")}</span>
                            <span className="session-room-name">{currentRoom?.label}</span>
                        </div>
                    </div>
                    <div className="session-header-right">
                        <Button className="close-button" onClick={handleMinimize}
                            style={{ padding: "0", margin: "0", maxWidth: "1.875rem", width: "1.875rem", minWidth: "1.25rem" }}>
                            <span className="material-symbols-outlined">close</span>
                        </Button>
                    </div>
                </div>

                <div className="session-content">
                    <span className="session-content-text">{t("productivity_session_description")}</span>
                    {subject && <div className="session-content-subject">{t("subject")}: {subject}</div>}
                </div>

                <div className="timer-container" >
                    <div className="timer-text">
                        <span>focus</span>
                        <span>{timeLeft} {t("remaining_min")}</span>
                    </div>
                    <div className="progress-session-bar">
                        <div className="progress-bar-fill" style={{ width: `${progress}%` }}></div>
                    </div>
                </div>

                {currentTrackData && (
                    <div className="session-audio-player">
                        <div className="player-left">
                            <div className="player-icon-wrapper" style={{ backgroundColor: currentTrackData.back }}>
                                <span className="material-symbols-outlined" style={{ color: currentTrackData.color, fontSize: '1.25rem' }}>
                                    {currentTrackData.icon}
                                </span>
                            </div>
                            <div className="player-info">
                                <span className="player-track-name">{t(currentTrackData.name)}</span>
                                <div>
                                    <span className="player-status-text">
                                        {isMusicPlaying ? t("play_back") : t("on_pause")}
                                    </span>
                                    <span className="player-status-text"
                                        onClick={() => { 
                                            handleTrackChange(); 
                                            setIsExpanded(false);}}
                                        style={{ cursor: 'pointer' }}>
                                        {t("change_bruit")}
                                    </span>
                                </div>

                            </div>
                        </div>

                        <div className="player-right">
                            <button className="player-control-btn" onClick={() => setIsMusicPlaying(!isMusicPlaying)}>
                                <span className="material-symbols-outlined">
                                    {isMusicPlaying ? 'pause_circle' : 'play_circle'}
                                </span>
                            </button>
                        </div>
                    </div>
                )}

                <div className="session-button-container">
                    <button type="button" className="session-button-stop" onClick={handleStopSession}>
                        {t("stop_session")}
                    </button>
                </div>
            </div>
        </div>
    );

    if (!sessionView || sessionView === "create") return null;

    if (sessionView === "progress") {
        
            return (
                <>
                <div className={`session-fab-container  ${isMobile ? "mobile-pos" : ""}`} onClick={() => setIsExpanded(true)}>
                    <div className={`session-fab-circle-progress ${isMobile ? 'mobile-pos' : ''}`} style={{ width: size, height: size, position: 'relative' }}>
                        <svg
                            viewBox={`0 0 ${size} ${size}`}
                            className="session-timer-svg">
                            <circle stroke="#E0E0E0" strokeWidth={strokeWidth} 
                            fill="transparent" r={radius} cx={center} cy={center} />
                            <circle stroke="#5D03DC" strokeWidth={strokeWidth}
                             fill="transparent" r={radius} cx={center} cy={center}
                                strokeDasharray={circumference} 
                                strokeDashoffset={strokeDashoffset} 
                                strokeLinecap="round"
                                style={{ transition: 'stroke-dashoffset 1s linear' }} />
                        </svg>
                        <span className="material-symbols-outlined session-fab-icon" 
                        style={{ position: 'relative', zIndex: 2 }}>headphones</span>
                    </div>
                    <div className="session-fab-text">{timeLeft}</div>
                </div>
      
    

          {isExpanded && isMobile &&(
                <div className="mobile-popup-overlay" style={{
                    position: 'fixed', top: 0, left: 0, width: '100vw', height: '100vh',
                    backgroundColor: 'rgba(0,0,0,0.5)', zIndex: 9999,
                    display: 'flex', justifyContent: 'center', alignItems: 'center'
                }} onClick={handleMinimize}>
                    <div onClick={(e) => e.stopPropagation()}>
                        {renderSessionModal()}
                    </div>
                </div>
           
            )}

       {isExpanded && !isMobile &&(
        renderSessionModal() )}
         </>
               );
    }
}